import React, { useEffect, useMemo, useState } from 'react';
import { Card, Button, Table, Tag, message, Typography, Space } from 'antd';
import { ArrowLeftOutlined, CloudDownloadOutlined, CheckCircleFilled } from '@ant-design/icons';
import { useLocation, useNavigate } from 'react-router-dom';
import { externalStoreAPI } from '../api';
import ShopifyIcon from './Utils/ShopifyIcon';
import WooCommerceIcon from './Utils/WooCommerceIcon';
import { BRAND_PRIMARY } from '../utils/brandColors';

const ORDERS_PAGE_SIZE = 10;

const ImportThirdPartyStoreOrder = () => {
  const { Paragraph } = Typography;
  const navigate = useNavigate();
  const location = useLocation();

  const searchParams = useMemo(() => new URLSearchParams(location.search), [location.search]);
  const shopDomain = searchParams.get('shopDomain');
  const platform = (searchParams.get('platform') || 'shopify').toLowerCase();

  const [ordersLoading, setOrdersLoading] = useState(false);
  const [ordersPage, setOrdersPage] = useState(1);
  const [ordersData, setOrdersData] = useState([]);
  const [ordersCache, setOrdersCache] = useState({});
  const [orderCursors, setOrderCursors] = useState({ 1: null });
  const [ordersPageInfoMap, setOrdersPageInfoMap] = useState({});
  const [selectedOrderKeys, setSelectedOrderKeys] = useState([]);
  const [importingOrders, setImportingOrders] = useState(false);
  const [showResult, setShowResult] = useState(false);
  const [importResultStats, setImportResultStats] = useState({
    importedCount: 0,
    unmappedCount: 0,
    skippedCount: 0,
  });

  useEffect(() => {
    if (!shopDomain) {
      message.error('Missing store information. Redirecting to My Stores.');
      navigate('/dropshipper/my-stores');
      return;
    }

    fetchUnfulfilledOrders(1, true);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [shopDomain]);

  const getOrderRowKey = (order) => {
    if (!order) return '';
    const key = order.legacyResourceId || order.id || order.name;
    return key ? String(key) : '';
  };

  const resetOrderState = () => {
    setOrdersPage(1);
    setOrdersData([]);
    setOrdersCache({});
    setOrderCursors({ 1: null });
    setOrdersPageInfoMap({});
    setSelectedOrderKeys([]);
    setShowResult(false);
  };

  const fetchUnfulfilledOrders = async (page = 1, force = false) => {
    if (!shopDomain) return;

    if (!force && ordersCache[page]) {
      setOrdersData(ordersCache[page]);
      setOrdersPage(page);
      return;
    }

    const params = {
      page_size: ORDERS_PAGE_SIZE,
    };
    const cursor = force ? null : orderCursors[page];
    if (cursor) {
      params.cursor = cursor;
    }

    setOrdersLoading(true);
    try {
      const response = await externalStoreAPI.getUnfulfilledOrders(platform, shopDomain, params);

      if (response.data.success) {
        const orders = response.data.orders || [];
        const pageInfo = response.data.pageInfo || {};

        setOrdersCache((prev) => ({
          ...prev,
          [page]: orders,
        }));
        setOrdersPageInfoMap((prev) => ({
          ...prev,
          [page]: pageInfo,
        }));
        setOrderCursors((prev) => ({
          ...prev,
          [page + 1]: pageInfo.endCursor || null,
        }));

        setOrdersData(orders);
        setOrdersPage(page);
      }
    } catch (error) {
      
      message.error('Failed to fetch Shopify orders');
    } finally {
      setOrdersLoading(false);
    }
  };

  const goToOrdersPage = async (targetPage) => {
    if (targetPage < 1) {
      return;
    }

    if (ordersCache[targetPage]) {
      setOrdersData(ordersCache[targetPage]);
      setOrdersPage(targetPage);
      return;
    }

    if (targetPage > 1 && typeof orderCursors[targetPage] === 'undefined') {
      message.warning('Please load pages sequentially.');
      return;
    }

    await fetchUnfulfilledOrders(targetPage);
  };

  const handleOrdersPrev = () => {
    if (ordersPage <= 1) return;
    goToOrdersPage(ordersPage - 1);
  };

  const handleOrdersNext = () => {
    const pageInfo = ordersPageInfoMap[ordersPage] || {};
    if (!pageInfo.hasNextPage) return;
    goToOrdersPage(ordersPage + 1);
  };

  const handleClearOrderSelection = () => {
    setSelectedOrderKeys([]);
  };

  const handleImportOrders = async () => {
    if (!shopDomain) return;
    if (!selectedOrderKeys.length) {
      message.warning('Please select at least one order to import');
      return;
    }

    setImportingOrders(true);

    try {
      const response = await externalStoreAPI.importOrders(
        platform,
        shopDomain,
        { order_ids: selectedOrderKeys },
      );

      if (response.data.success) {
        const results = response.data.results || [];
        const importedCount = response.data.imported_count || 0;
        const totalSelected = selectedOrderKeys.length;
        const skippedCount = results.filter((item) => item.status === 'skipped').length;
        const unmappedCount = Math.max(totalSelected - importedCount - skippedCount, 0);

        setImportResultStats({
          importedCount,
          unmappedCount,
          skippedCount,
        });

        resetOrderState();
        setShowResult(true);
      } else {
        message.error('Failed to import Shopify orders');
      }
    } catch (error) {
      
      const errorDetail = error.response?.data?.detail || 'Failed to import Shopify orders';
      message.error(errorDetail);
    } finally {
      setImportingOrders(false);
    }
  };

  const handleNavigateFromResult = (path) => {
    navigate(path);
  };

  const handleImportAnother = () => {
    resetOrderState();
    fetchUnfulfilledOrders(1, true);
  };

  const getPlatformTag = () => {
    if (platform === 'shopify') {
      return (
        <Tag
          icon={<ShopifyIcon />}
          style={{
            backgroundColor: '#f0f9f0',
            borderColor: '#96bf48',
            color: '#2d5a1d',
          }}
        >
          {platform.toUpperCase()}
        </Tag>
      );
    }

    if (platform === 'woocommerce') {
      return (
        <Tag
          icon={<WooCommerceIcon />}
          style={{
            backgroundColor: '#f3e5f5',
            borderColor: '#9c27b0',
            color: '#6a1b9a',
          }}
        >
          {platform.toUpperCase()}
        </Tag>
      );
    }

    return <Tag color={BRAND_PRIMARY}>{platform.toUpperCase()}</Tag>;
  };

  const orderColumns = [
    {
      title: 'Order',
      dataIndex: 'name',
      key: 'name',
      width: '18%',
      render: (name, record) => (
        <div>
          <div>{name || '-'}</div>
          <div style={{ color: '#8c8c8c', fontSize: 12 }}>
            {record.legacyResourceId ? `#${record.legacyResourceId}` : ''}
          </div>
        </div>
      ),
    },
    {
      title: 'Items',
      key: 'items',
      width: '42%',
      render: (_, record) => {
        const items = record.lineItems || [];
        if (!items.length) return '-';
        return (
          <div>
            {items.map((item, index) => {
              const image = item.productImage || {};
              const imageUrl = image.url;
              const altText = image.altText || item.title || 'Product image';
              const displayText = `${item.title || 'Item'} × ${item.quantity || 0}`;

              return (
                <div
                  key={`${record.id || record.name || 'order'}-${index}`}
                  style={{
                    marginBottom: index === items.length - 1 ? 0 : 12,
                    display: 'flex',
                    alignItems: 'center',
                    gap: 12,
                  }}
                >
                  {imageUrl ? (
                    <img
                      src={imageUrl}
                      alt={altText}
                      style={{
                        width: 32,
                        height: 32,
                        objectFit: 'cover',
                        borderRadius: 6,
                        border: '1px solid #f0f0f0',
                        padding: 2,
                        background: '#fff',
                      }}
                    />
                  ) : (
                    <div style={{ width: 32 }} />
                  )}
                  <Paragraph
                    ellipsis={{
                      rows: 2,
                      tooltip: displayText,
                    }}
                    style={{
                      marginBottom: 0,
                      fontSize: 13,
                      color: '#262626',
                      flex: 1,
                    }}
                  >
                    {displayText}
                  </Paragraph>
                </div>
              );
            })}
          </div>
        );
      },
    },
    {
      title: 'Recipient',
      key: 'recipient',
      dataIndex: 'shippingAddress',
      width: '18%',
      render: (shippingAddress) => {
        const name = shippingAddress?.name;
        return name ? <span>{name}</span> : '-';
      },
    },
    {
      title: 'Country',
      key: 'country',
      dataIndex: 'shippingAddress',
      width: '12%',
      render: (shippingAddress) => shippingAddress?.country || '-',
    },
    {
      title: 'Created At',
      dataIndex: 'createdAt',
      key: 'createdAt',
      width: '10%',
      render: (value) => {
        if (!value) return '-';
        return new Date(value).toLocaleDateString('zh-CN', {
          year: 'numeric',
          month: '2-digit',
          day: '2-digit',
          hour: '2-digit',
          minute: '2-digit',
        });
      },
    },
  ];

  const currentOrdersPageInfo = ordersPageInfoMap[ordersPage] || {};

  return (
    <div style={{ padding: 24 }}>
      <Card
        title={
          <Space size="middle">
            <Button type="text" icon={<ArrowLeftOutlined />} onClick={() => navigate(-1)}>
              Back
            </Button>
            <span>
              {showResult
                ? 'Import Result'
                : `Import Unfulfilled Orders from ${shopDomain || ''}`}
            </span>
          </Space>
        }
        extra={getPlatformTag()}
      >
        {showResult ? (
          <div
            style={{
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              justifyContent: 'center',
              padding: '40px 16px',
              textAlign: 'center',
            }}
          >
            <CheckCircleFilled style={{ fontSize: 80, color: '#52c41a', marginBottom: 24 }} />
            <Typography.Title level={3} style={{ marginBottom: 16 }}>
              Import Completed
            </Typography.Title>
            <div style={{ lineHeight: 1.6 }}>
              <div>
                {`${importResultStats.importedCount} orders are created. You can check at `}
                <Button
                  type="link"
                  style={{ padding: 0 }}
                  onClick={() => handleNavigateFromResult('/dropshipper/orders')}
                >
                  My Orders
                </Button>
              </div>
              <div style={{ marginTop: 8 }}>
                {`${importResultStats.unmappedCount} orders did not link to any products. You can check at `}
                <Button
                  type="link"
                  style={{ padding: 0 }}
                  onClick={() => handleNavigateFromResult('/dropshipper/unmapped-orders')}
                >
                  Unmapped Orders
                </Button>
              </div>
              <div style={{ marginTop: 8 }}>
                {`${importResultStats.skippedCount} duplicated orders have been skipped.`}
              </div>
            </div>

            <Space style={{ marginTop: 32 }}>
              <Button onClick={() => navigate('/dropshipper/my-stores')}>
                Back to My Stores
              </Button>
              <Button type="primary" onClick={handleImportAnother}>
                Import More Orders
              </Button>
            </Space>
          </div>
        ) : (
          <>
            <div
              style={{
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center',
                marginBottom: 12,
              }}
            >
              <Tag color={BRAND_PRIMARY}>Selected {selectedOrderKeys.length}</Tag>
              <Space>
                <Button onClick={() => fetchUnfulfilledOrders(1, true)} loading={ordersLoading}>
                  Refresh
                </Button>
                <Button
                  size="small"
                  onClick={handleClearOrderSelection}
                  disabled={!selectedOrderKeys.length}
                >
                  Clear Selection
                </Button>
              </Space>
            </div>

            <Table
              columns={orderColumns}
              dataSource={ordersData}
              loading={ordersLoading}
              rowKey={getOrderRowKey}
              pagination={false}
              rowSelection={{
                selectedRowKeys: selectedOrderKeys,
                onChange: (keys) => setSelectedOrderKeys(keys.map(String)),
                preserveSelectedRowKeys: true,
              }}
              locale={{
                emptyText: ordersLoading ? 'Loading orders...' : 'No unfulfilled orders found',
              }}
            />

            <div
              style={{
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center',
                marginTop: 16,
              }}
            >
              <Button onClick={handleOrdersPrev} disabled={ordersPage <= 1 || ordersLoading}>
                Previous
              </Button>
              <span>Page {ordersPage}</span>
              <Button
                type="primary"
                onClick={handleOrdersNext}
                disabled={ordersLoading || !currentOrdersPageInfo.hasNextPage}
              >
                Next
              </Button>
            </div>

            <div style={{ marginTop: 24, textAlign: 'right' }}>
              <Space>
                <Button onClick={() => navigate('/dropshipper/my-stores')}>
                  Cancel
                </Button>
                <Button
                  type="primary"
                  icon={<CloudDownloadOutlined />}
                  onClick={handleImportOrders}
                  disabled={!selectedOrderKeys.length}
                  loading={importingOrders}
                >
                  {selectedOrderKeys.length ? `Import (${selectedOrderKeys.length})` : 'Import'}
                </Button>
              </Space>
            </div>
          </>
        )}
      </Card>
    </div>
  );
};

export default ImportThirdPartyStoreOrder;
